import { useEffect, useState, useMemo } from "preact/hooks";
import { Clock, BadgeCheck } from "lucide-preact";
import Logo from "./Logo";
import { toTitleCase } from "@/lib/strings";
import { getL11nString } from "@/l11n/l11n";
import { IS_NARROW_CUTOFF } from "@/lib/globals";

const SkuOptions = ({ message, onSkuSelected }) => {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isNarrow, setIsNarrow] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    skus,
    testimonials,
    meta,
    timerDurationMin,
    timerTurnRedAt,
    registered_office,
  } = useMemo(
    () => ({
      skus: message.replyOptions,
      testimonials: message.xp.testimonials,
      meta: message.xp.meta,
      timerDurationMin: message.xp.timerDurationMin,
      timerTurnRedAt: message.xp.timerTurnRedAt,
      registered_office: getL11nString("registered_office"),
    }),
    [message]
  );

  useEffect(() => {
    const updateWidth = () => setIsNarrow(window.innerWidth < IS_NARROW_CUTOFF);
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  useEffect(() => {
    // Set the timer to the duration of the timer
    setSecondsLeft(timerDurationMin * 60);

    // Set the timer to turn red at the timerTurnRedAt value
    const timer = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleModalOpen = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  return (
    <div
      style={{
        fontFamily: "sans-serif",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden", // Prevent double scrollbars
      }}
    >
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px 16px 32px 16px", // Extra padding at bottom for better scrolling
          WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
        }}
      >
        {/* Logo */}
        <div className="mb-4">
          <Logo />
        </div>

        {/* Countdown Section */}
        {CountdownSection(meta, secondsLeft, timerTurnRedAt)}

        {/* Headline and subheadline */}
        {HeadlineAndSubheadline(meta)}

        {/* Money-Back Guarantee Link */}
        {MoneyBackGuaranteeLink(meta, handleModalOpen)}

        {/* SKU tiles */}
        {SkuTileRow(skus, onSkuSelected, isNarrow)}

        {/* Laurels */}
        {Laurels(skus[0], onSkuSelected)}

        {/* What You Get */}
        {WhatYouGet(meta.whatYouGetStrings ?? {})}

        {/* Testimonial */}
        {TestimonialSection(testimonials, meta)}

        {/* Guarantee */}
        {GuaranteeSection(meta, handleModalOpen, handleModalClose, isModalOpen)}

        {/* Little Tile Column */}
        {SkuColumn(skus, onSkuSelected)}

        {/* Purchase Confidence */}
        {PurchaseConfidence(meta, registered_office)}

        {/* Guarantee Modal */}
        {isModalOpen && (
          <div
            className="modal fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
            style={{ zIndex: 9999 }}
            onClick={handleModalClose}
          >
            <div
              className="modal-content bg-white text-black p-4 text-lg mx-6"
              style={{
                maxWidth: "26rem",
                position: "relative",
                zIndex: 10000,
                borderRadius: 22,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4 flex flex-row items-center justify-center gap-2">
                <img
                  src="/assets/i/7-day-badge@2x.png"
                  alt="7 Day Money Back Guarantee"
                  style={{ width: "80px" }}
                />
                <div
                  className="mt-2 mb-0"
                  style={{
                    fontWeight: 700,
                    fontSize: 24,
                    maxWidth: 200,
                    wordWrap: "break-word",
                  }}
                >
                  {meta.guarantee_button}
                </div>
              </div>
              <p>{meta.guarantee_expand}</p>
              <button className="text-white bg-blue-500 mt-3" onClick={handleModalClose}>
                OK
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// MARK: Countdown

const CountdownSection = (meta, secondsLeft, timerTurnRedAt) => {
  return (
    <>
      {/* Limited offer banner */}
      <div
        style={{
          // background: `hsl(var(--reddish))`,
          background: "#F97A7A",
          color: "#fff",
          padding: "6px 12px",
          borderRadius: 4,
          display: "inline-block",
          fontSize: 15,
          marginBottom: 8,
        }}
      >
        <span dangerouslySetInnerHTML={{ __html: meta.limited_offer_banner }} />
      </div>

      {/* Timer */}
      <div
        style={{
          fontSize: 15,
          marginBottom: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
        }}
      >
        <Clock size={18} strokeWidth={2} />
        <span>
          {meta.offer_valid}{" "}
          <span
            style={{
              fontWeight: 600,
              color: secondsLeft > timerTurnRedAt ? "black" : "red",
            }}
          >
            {formatTime(secondsLeft)}
          </span>
        </span>
      </div>
    </>
  );
};

// MARK: Headline and subheadline

const HeadlineAndSubheadline = (meta) => {
  return (
    <div
      style={{
        textAlign: "center",
        maxWidth: 460,
        marginInline: "auto",
      }}
      className="mt-6 mb-4"
    >
      {/* Headline */}
      <div
        style={{
          lineHeight: 1.3,
          marginBottom: 4,
          fontSize: `clamp(1rem, 5vw, 1.5rem)`,
          fontWeight: 700,
        }}
      >
        <span dangerouslySetInnerHTML={{ __html: meta.headline }} />
      </div>

      {/* Subheadline */}
      <div
        style={{
          color: "#444",
          lineHeight: 1.4,
          fontSize: `clamp(0.75rem, 3.25vw, 1rem)`,
        }}
      >
        {meta.subheadline}
      </div>
    </div>
  );
};

// MARK: Money-Back Guarantee Link

const MoneyBackGuaranteeLink = (meta, handleModalOpen) => {
  return (
    <div className="mt-4 mb-6">
      <a
        href="#"
        onClick={handleModalOpen}
        style={{
          color: "black",
          fontWeight: 600,
          fontSize: 18,
          textDecoration: "underline",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "hsl(var(--purple-dark))")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "grey")}
      >
        {meta.hundred_percent_money_back}
      </a>
    </div>
  );
};

// MARK: Sku Tile Row

const SkuTileRow = (skus, onSkuSelected, isNarrow) => {
  return (
    <div
      className="mt-6 mb-6"
      style={{
        display: "flex",
        flexDirection: isNarrow ? "column" : "row",
        gap: 8,
        justifyContent: "center",
        alignItems: "center",
        flexWrap: "nowrap",
      }}
    >
      {/* Tile Each SKU */}
      {skus.map((sku) => SkuTileVertical(sku, onSkuSelected))}
    </div>
  );
};

// MARK: Sku Tile

const SkuTileVertical = (sku, onSkuSelected) => {
  const highlighted = sku.xp.highlighted === true;
  const tag = sku.xp.localized_tags?.[1];

  return (
    <div
      key={sku.selectionIntent}
      style={{
        position: "relative",
        borderRadius: 12,
      }}
    >
      {highlighted && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: 16,
            boxShadow: "0 0 30px 10px rgba(253, 216, 53, 0.5)",
            zIndex: 0,
          }}
        />
      )}

      <div
        onClick={() => onSkuSelected(sku)}
        style={{
          position: "relative",
          zIndex: 1,
          isolation: "isolate",
          borderRadius: highlighted || tag ? "12px 12px 0 0" : 12,
          paddingTop: 16,
          paddingBottom: 12,
          // paddingLeft: 6,
          // paddingRight: 10,
          width: highlighted ? 150 : 104,
          cursor: "pointer",
          background: "#fff",
          border: highlighted
            ? "3px solid #fdd835"
            : tag
            ? "1.5px solid #79CF81"
            : "1.5px solid #ccc",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          transition: "background-color 0.2s ease, border 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#fff3cd"; // Darker amber background
          e.currentTarget.style.border = highlighted
            ? "3px solid hsl(var(--purple-dark))"
            : "1px solid hsl(var(--purple-dark))";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "#fff"; // Reset to white background
          e.currentTarget.style.border = highlighted
            ? "3px solid #fdd835"
            : "1px solid #ccc"; // Reset to original border
        }}
      >
        {/* SKU Headline */}
        <div
          style={{
            fontSize: highlighted
              ? `clamp(0.75rem, 5vw, 1.5rem)`
              : `clamp(0.5rem, 3.5vw, 1rem)`,
            fontWeight: "bold",
            marginBottom: 0,
            lineHeight: 1.25,
          }}
        >
          {sku.xp.headline}
        </div>

        {/* SKU Description */}
        {sku.xp.description && (
          <div style={{ fontSize: 12, marginTop: 8, marginBottom: 0, lineHeight: 1.25 }}>
            {sku.xp.description}
          </div>
        )}

        {/* Preprice */}
        {sku.xp.preprice && (
          <div
            style={{
              fontSize: 12,
              marginTop: 16,
            }}
          >
            <span dangerouslySetInnerHTML={{ __html: sku.xp.preprice }} />
          </div>
        )}

        {/* SKU Price */}
        <div
          style={{
            fontSize: 20,
            fontWeight: "bold",
            lineHeight: 1,
            marginTop: 6,
          }}
        >
          {sku.xp.price}
        </div>

        {/* SKU Postprice */}
        {sku.xp.postprice && (
          <div
            style={{
              fontSize: 10,
              marginTop: 2,
              lineHeight: 1.25,
            }}
          >
            <span dangerouslySetInnerHTML={{ __html: sku.xp.postprice }} />
          </div>
        )}

        {/* SKU CTA */}
        {sku.xp.c2a && (
          <div
            style={{
              marginTop: 12,
              background: `hsl(var(--purple-dark))`,
              color: "white",
              borderRadius: 999,
              padding: "4px 12px",
              fontSize: 15,
              fontWeight: 800,
              width: "120px",
              maxWidth: "80%",
            }}
          >
            {sku.xp.c2a}
          </div>
        )}
      </div>
      {tag && (
        <div
          style={{
            fontSize: 14,
            fontWeight: 900,
            backgroundColor: highlighted ? "#fdd835" : "#79CF81",
            color: highlighted ? "#BE6B00" : "#208100",
            padding: "4px 12px 6px 12px",
            borderRadius: "0 0 12px 12px",
            textTransform: "uppercase",
            letterSpacing: 2.0,
            maxWidth: highlighted ? 150 : 104,
            wordWrap: "break-word",
            lineHeight: 1,
          }}
        >
          {tag}
        </div>
      )}
    </div>
  );
};

// MARK: Testimonials

const TestimonialSection = (testimonials, meta) => {
  return (
    <div
      className="mt-12"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        className="text-xl font-bold mb-2"
        style={{
          maxWidth: 360,
          wordWrap: "break-word",
          lineHeight: 1.25,
          letterSpacing: "-0.5px",
        }}
      >
        {meta.testimonial_section_header}
      </div>
      {testimonials.map((testimonial) => TestimonialItem(testimonial))}
    </div>
  );
};

const TestimonialItem = (testimonial) => {
  return (
    <div
      style={{
        maxWidth: 360,
        margin: "0 auto",
        textAlign: "left",
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
      }}
    >
      <img
        src={testimonial.image}
        alt={testimonial.name}
        style={{
          width: 60,
          height: 60,
          borderRadius: "50%",
          objectFit: "cover",
        }}
      />
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          lineHeight: 1,
        }}
      >
        <div
          style={{
            fontStyle: "italic",
            fontSize: 16,
          }}
        >
          “{testimonial.quote}”
        </div>
        <div
          style={{
            fontSize: 16,
            fontWeight: 600,
          }}
        >
          - {testimonial.name}
        </div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          ⭐️⭐️⭐️⭐️⭐️
        </div>
      </div>
    </div>
  );
};

// MARK: Guarantee

const GuaranteeSection = (meta, handleModalOpen) => {
  return (
    <div className="mt-10 mb-10">
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0,
        }}
      >
        <img
          src="/assets/i/7-day-badge@2x.png"
          alt="7 Day Money Back Guarantee"
          style={{ width: "200px" }}
        />
        <div
          className="mt-2 mb-0"
          style={{
            fontWeight: 700,
            fontSize: 17,
          }}
        >
          {toTitleCase(meta.hundred_percent_money_back)}
        </div>
        <div
          style={{
            background: "#ddd",
            color: "#333",
            borderRadius: 999,
            padding: "3px 20px",
            cursor: "pointer",
            maxWidth: 320,
            transition: "background 0.3s ease",
            marginTop: 10,
          }}
          onClick={handleModalOpen}
          onMouseEnter={(e) => (e.currentTarget.style.background = `hsla(var(--peach))`)}
          onMouseLeave={(e) => (e.currentTarget.style.background = `#ddd`)}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: 14,
              marginBottom: 0,
            }}
          >
            {meta.refund_policy}
          </div>
        </div>
      </div>
    </div>
  );
};

// MARK: Little Tile Row

const SkuColumn = (skus, onSkuSelected) => {
  return (
    <div className="flex flex-col items-center justify-center gap-4 mt-6 mb-8">
      {skus.map((sku) => HorizontalSkuTile(sku, onSkuSelected))}
    </div>
  );
};

// MARK: Little Tile

const HorizontalSkuTile = (sku, onSkuSelected) => {
  return (
    <div
      key={sku.selectionIntent}
      style={{
        position: "relative",
        borderRadius: 12,
      }}
    >
      <div
        onClick={() => onSkuSelected(sku)}
        style={{
          position: "relative",
          zIndex: 1,
          isolation: "isolate",
          borderRadius: 12,
          padding: "10px 16px",
          width: 300,
          background: "#F9EDFF",
          cursor: "pointer",
          border: "1px solid #ccc",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          transition: "background-color 0.2s ease, border 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#fff3cd"; // Darker amber background
          e.currentTarget.style.border = "1px solid hsl(var(--purple-dark))";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "#F9EDFF";
          e.currentTarget.style.border = "1px solid #ccc"; // Reset to original border
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {/* SKU Headline */}
          <div
            style={{
              fontSize: `clamp(0.5rem, 3.5vw, 1rem)`,
              fontWeight: "bold",
              marginBottom: 0,
              lineHeight: 1.25,
            }}
          >
            {sku.xp.headline}
          </div>

          {/* SKU CTA */}
          {sku.xp.c2a && (
            <div
              style={{
                marginTop: 12,
                background: `hsl(var(--purple-dark))`,
                color: "white",
                borderRadius: 999,
                padding: "4px 12px",
                fontSize: 15,
                fontWeight: 800,
                width: "120px",
                maxWidth: "80%",
              }}
            >
              {sku.xp.c2a}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Preprice */}
          {sku.xp.preprice && (
            <div
              style={{
                fontSize: 12,
              }}
            >
              <span dangerouslySetInnerHTML={{ __html: sku.xp.preprice }} />
            </div>
          )}

          {/* SKU Price */}
          <div
            style={{
              fontSize: 20,
              fontWeight: "bold",
              lineHeight: 1,
              marginTop: 0,
            }}
          >
            {sku.xp.price}
          </div>

          {/* SKU Postprice */}
          {sku.xp.postprice && (
            <div
              style={{
                fontSize: 10,
                marginTop: 2,
                lineHeight: 1.25,
              }}
            >
              <span dangerouslySetInnerHTML={{ __html: sku.xp.postprice }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// MARK: Confidence

const PurchaseConfidence = (meta, registered_office) => {
  return (
    <div className="flex flex-col items-center justify-center gap-4 mt-6">
      <div className="flex flex-row items-center justify-center gap-1">
        <div
          className="text-center  text-black-500"
          style={{ fontWeight: 600, fontSize: 15 }}
        >
          {meta.guaranteed_safe_checkout}
        </div>
        <img
          src="/assets/i/stripe-check@2x.png"
          alt="Stripe Checkout"
          style={{ height: "22px" }}
        />
      </div>
      <div className="flex flex-row items-center justify-center gap-6 mt-2">
        <img
          src="/assets/i/apple_pay@2x.png"
          alt="Apple Pay"
          style={{ height: "26px" }}
        />
        <img
          src="/assets/i/google_pay@2x.png"
          alt="Google Pay"
          style={{ height: "26px" }}
        />
        <img src="/assets/i/visa@2x.png" alt="Visa" style={{ height: "21px" }} />
        <img
          src="/assets/i/mastercard@2x.png"
          alt="Mastercard"
          style={{ height: "26px" }}
        />
      </div>
      <div className="mt-5">
        <img
          src="/assets/i/secure-ssl@2x.png"
          alt="Secure SSL"
          style={{ height: "60px" }}
        />
      </div>
      <div
        className="text-center text-gray-500 mt-2"
        style={{ fontSize: 12, maxWidth: 260, wordWrap: "break-word", lineHeight: 1.25 }}
      >
        {registered_office}
      </div>
    </div>
  );
};

// MARK: WhatYouGet

const WhatYouGet = ({ whatYouGetStrings }) => {
  whatYouGetStrings = {
    title: "What You Get",
    subtitle: "You get a lot of stuff",
    items: [
      { title: "Item 1", description: "Description 1" },
      { title: "Item 2", description: "Description 2" },
      { title: "Item 3", description: "Description 3" },
    ],
    footer: "Everything in one mobile app on your device (iOS 16.4+ or Android 8.0+).",
  };
  return (
    <div
      className="text-sm text-[#003B38] mt-14 mb-10 flex flex-col items-center justify-center mx-auto"
      style={{ maxWidth: "360px" }}
    >
      <div className="text-xl font-bold mb-2" style={{ letterSpacing: "-0.5px" }}>
        {whatYouGetStrings.title}
      </div>
      <div className="mb-4">{whatYouGetStrings.subtitle}</div>
      <ul className="space-y-6 w-full">
        {whatYouGetStrings.items.map(({ title, description }) => (
          <li
            className="flex items-start gap-3"
            key={title}
            style={{ textAlign: "left" }}
          >
            <BadgeCheck
              className="w-6 h-6 mt-1 text-[#00B9A2] shrink-0"
              strokeWidth={3}
            />
            <div>
              <h3 className="font-semibold text-[#003B38]">{title}</h3>
              <p className="text-[#003B38] mt-1 leading-snug">{description}</p>
            </div>
          </li>
        ))}
      </ul>
      <p className="mt-6 text-[#003B38] font-semibold whitespace-pre-line">
        {whatYouGetStrings.footer}
      </p>
    </div>
  );
};

// MARK: Laurels

const Laurels = (sku, onSkuSelected) => {
  return (
    <div className="flex flex-row items-center justify-center mt-16 mb-8 mr-2 ml-2">
      <div
        className="flex flex-col items-center justify-center gap-0"
        style={{
          backgroundColor: "#f7f7f7", // very faint grey
          borderRadius: 22,
          padding: "16px",
        }}
      >
        <div
          className="flex flex-row items-center justify-center gap-20"
          style={{
            padding: "16px",
            position: "relative",
          }}
        >
          <img
            src="/assets/i/laurel-left.svg"
            alt="Laurel Left"
            style={{
              width: "auto",
              height: "auto",
              imageRendering: "crisp-edges",
            }}
          />
          <img
            src="/assets/i/laurel-right.svg"
            alt="Laurel Right"
            style={{
              width: "auto",
              height: "auto",
              imageRendering: "crisp-edges",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "45%",
              left: "50%",
              transform: "translate(-45%, -50%)",
              color: "#00696D",
            }}
          >
            <div
              style={{
                position: "relative",
                height: "100px",
                width: "100%",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: "32%",
                  transform: "translateX(-50%) translateY(-6px)",
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                #
              </div>
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: "45%",
                  transform: "translateX(calc(-50%)) translateY(-30px)",
                  fontSize: 100,
                  fontWeight: 800,
                }}
              >
                1
              </div>
            </div>
            <div style={{ fontSize: 18, fontWeight: 500, marginTop: 0 }}>
              [hyper-personalized
              <br />
              language app*]
            </div>
          </div>
        </div>
        <div
          className="flex flex-col items-center justify-center gap-2 text-center"
          style={{ color: "#00696D" }}
        >
          <div className="text-xl font-bold mt-2">
            [Build Confidence in targetLang Faster]
          </div>
          <div
            style={{
              fontSize: 12,
              maxWidth: 260,
              wordWrap: "break-word",
              lineHeight: 1.25,
            }}
          >
            * source
          </div>
        </div>
        <div
          onClick={() => onSkuSelected(sku)}
          className="mt-5 mb-2"
          style={{
            background: `hsl(var(--purple-dark))`,
            color: "white",
            borderRadius: 999,
            padding: "8px 12px",
            fontSize: 18,
            fontWeight: 800,
            width: 240,
            cursor: "pointer",
            transition: "background 0.3s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = `hsla(var(--peach))`)}
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = `hsl(var(--purple-dark))`)
          }
        >
          {sku.xp.headline}
        </div>
      </div>
    </div>
  );
};

// MARK: Helpers

const formatTime = (s) => {
  const m = String(Math.floor(s / 60));
  const sec = String(s % 60).padStart(2, "0");
  return `${m}:${sec}`;
};

export default SkuOptions;

//   {
//     sku.strike_price && (
//       <div
//         style={{
//           fontSize: 14,
//           color: "#000",
//           textDecoration: "line-through",
//         }}
//       >
//         {sku.strike_price}
//       </div>
//     );
//   }

//   {
//     meta.guarantee_button && (
//       <div className="w-full">
//         <a href="#" onClick={handleModalOpen}>
//           {meta.guarantee_button}
//         </a>
//       </div>
//     );
//   }

//   /* <a
//   href="#"
//   onClick={handleModalOpen}
//   style={{
//     color: "grey",
//     fontWeight: 400,
//     fontSize: 14,
//   }}
//   onMouseEnter={(e) => (e.currentTarget.style.color = "hsl(var(--purple-dark))")}
//   onMouseLeave={(e) => (e.currentTarget.style.color = "grey")}
// >
//   {meta.refund_policy}
// </a>; */

//   /* <div
//   className="mt-2 mb-0"
//   style={{
//     color: "black",
//     fontWeight: 600,
//     fontSize: 17,
//     marginBottom: 0,
//   }}
// >
//   {meta.guarantee_button}
// </div> */
